<template>
  <!-- Mobile: stacked, normal page scroll -->
  <div class="tw-flex tw-flex-col tw-gap-6 lg:tw-hidden">
    <slot name="documents" />
    <slot name="details" />
  </div>

  <!-- Desktop: height pinned to the real remaining viewport so the panes scroll,
       never the page. Height is measured from the element's top offset. -->
  <div
    v-if="isDesktop"
    ref="desktopWrapper"
    class="tw-hidden lg:tw-block tw-overflow-hidden"
    :style="{ height: availableHeight }"
  >
    <ResizableGroup
      direction="horizontal"
      class="tw-flex tw-h-full tw-w-full tw-overflow-hidden tw-rounded-2xl tw-border tw-border-surface-200 dark:tw-border-neutral-800 tw-bg-white dark:tw-bg-neutral-950 tw-shadow-[0_1px_3px_rgba(20,21,36,0.04),0_8px_24px_-12px_rgba(20,21,36,0.10)]"
      @layout="handleLayout"
    >
      <ResizablePanel
        :default-size="documentSize"
        :min-size="minDocumentSize"
        :max-size="maxDocumentSize"
      >
        <div
          class="tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-overflow-hidden tw-p-5 tw-bg-surface-50/60 dark:tw-bg-neutral-900/40"
        >
          <slot name="documents" />
        </div>
      </ResizablePanel>
      <ResizableHandle class="tw-bg-surface-200 dark:tw-bg-neutral-800" />
      <ResizablePanel :default-size="100 - documentSize" :min-size="detailsMinSize">
        <div class="tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-overflow-hidden tw-px-5 tw-py-4">
          <slot name="details" />
        </div>
      </ResizablePanel>
    </ResizableGroup>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { ResizableGroup, ResizableHandle, ResizablePanel } from '@/components/ui/resizable';

interface Props {
  storageKey?: string;
  defaultDocumentSize?: number;
  /** Smallest (%) the documents pane may shrink to. */
  minDocumentSize?: number;
  /** Hard cap (%) on the documents pane — the details pane never shrinks below 100 - this. */
  maxDocumentSize?: number;
  /** Gap (px) left below the panel before the viewport edge. */
  bottomGap?: number;
  /** Smallest height (px) the panel is allowed to shrink to. */
  minHeight?: number;
}

const props = withDefaults(defineProps<Props>(), {
  storageKey: 'entity-split-document-size',
  defaultDocumentSize: 55,
  minDocumentSize: 30,
  bottomGap: 16,
  minHeight: 420,
});

const detailsMinSize = computed(() =>
  props.maxDocumentSize != null ? 100 - props.maxDocumentSize : 25
);

const isDesktop = ref(false);
const documentSize = ref(props.defaultDocumentSize);
const desktopWrapper = ref<HTMLElement | null>(null);
const availableHeight = ref('auto');

const updateViewport = () => {
  isDesktop.value = window.innerWidth >= 1024;
  recomputeHeight();
};

const recomputeHeight = () => {
  if (!isDesktop.value) {
    availableHeight.value = 'auto';
    return;
  }
  nextTick(() => {
    const el = desktopWrapper.value;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    const h = Math.max(props.minHeight, window.innerHeight - top - props.bottomGap);
    availableHeight.value = `${Math.round(h)}px`;
  });
};

const handleLayout = (sizes: number[]) => {
  if (!sizes.length) return;
  documentSize.value = sizes[0];
  localStorage.setItem(props.storageKey, String(sizes[0]));
};

onMounted(() => {
  updateViewport();
  window.addEventListener('resize', updateViewport);
  const stored = localStorage.getItem(props.storageKey);
  if (stored) {
    const max = props.maxDocumentSize ?? 100;
    documentSize.value = Math.min(max, Math.max(props.minDocumentSize, Number(stored)));
  }
  // Re-measure once layout/fonts settle (header height can shift on first paint).
  requestAnimationFrame(recomputeHeight);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateViewport);
});
</script>
